import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTimer } from "@/contexts/TimerContext";
import { TaskProperties } from "@/lib/taskProperties";

const PRIORITY_DOT = { low: "bg-muted-foreground/30", normal: "bg-primary/60", high: "bg-warning", urgent: "bg-destructive ring-2 ring-destructive/20" };
const ROW_MAX = { compact: 4, comfortable: 7, detailed: 20 };

// Canonical premium task row (Today / Personal / Assigned / Waiting / Completed
// / List). Title dominant; populated properties fill available width. Row body
// click opens View Task; only the isolated leading checkbox completes the task.
export function TaskRow({ t, density = "comfortable", ctx = "all", cfg, catName, cats, members, onOpen, onComplete, onChanged }) {
  const done = t.status === "done";
  const timer = useTimer();
  const running = (id) => !!(timer?.timer?.active && timer.timer.task_id === id && timer.timer.state === "running");
  const dens = cfg?.density || density;
  return (
    <div onClick={() => onOpen(t.id)} data-testid={`task-${t.id}`}
      className={cn("group relative flex cursor-pointer flex-col transition-colors hover:bg-muted/40",
        dens === "compact" ? "px-3 py-2" : dens === "detailed" ? "px-4 py-3.5" : "px-4 py-3")}>
      <div className="flex min-h-[52px] items-center gap-3">
        <button onClick={(e) => { e.stopPropagation(); onComplete(t); }} data-testid={`complete-${t.id}`}
          className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all",
            done ? "bg-success text-white" : "border border-border hover:border-accent hover:bg-accent-subtle")}>
          {done && <Check className="h-3 w-3" />}
        </button>
        <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", PRIORITY_DOT[t.priority] || PRIORITY_DOT.normal)} title={`Priority: ${t.priority || "normal"}`} />
        <div className={cn("flex min-w-0 flex-1 gap-x-4", dens === "compact" ? "flex-row items-center" : "flex-col sm:flex-row sm:items-center sm:justify-between")}>
          <div className={cn("truncate text-base font-medium leading-snug transition-colors", done ? "text-muted-foreground line-through opacity-70" : "text-foreground group-hover:text-primary", dens === "compact" ? "flex-1" : "sm:flex-1")}>
            {t.in_inbox && <span className="mr-1.5 rounded bg-accent-subtle px-1.5 text-[10px] text-accent align-middle">Inbox</span>}
            {t.title}
          </div>
          <div className={cn("min-w-0 shrink-0", dens === "compact" ? "" : "mt-1 sm:mt-0")}>
            <TaskProperties t={t} ctx={ctx} density={dens} cfg={cfg} catName={catName} cats={cats} members={members} running={running} max={ROW_MAX[dens] || 7} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function TaskListPremium({ tasks, density = "comfortable", ctx = "all", cfg, catName, cats, members, onOpen, onComplete, onChanged }) {
  return (
    <div className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/80 bg-card shadow-soft" data-testid="task-list">
      {tasks.map((t) => <TaskRow key={t.id} t={t} density={density} ctx={ctx} cfg={cfg} catName={catName} cats={cats} members={members} onOpen={onOpen} onComplete={onComplete} onChanged={onChanged} />)}
    </div>
  );
}
